import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import {
  docChatCapabilitiesUrl,
  type DocChatCapabilityManifest,
  DocChatCapabilityManifestSchema,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatCapabilities'
import { useGeckoPermissions } from 'features/permissions'
import useSWR from 'swr'

type DocChatCapabilitiesKey = readonly [string, string, string]

async function fetchDocChatCapabilities(
  [url]: DocChatCapabilitiesKey,
  axios: ReturnType<typeof useGeckoAuth>['authorizedAxios']
): Promise<DocChatCapabilityManifest> {
  const response = await axios.get(url)
  return DocChatCapabilityManifestSchema.parse(response.data)
}

export function useDocChatCapabilities(siteId: string) {
  const { POWER_BACKEND_AI_URL } = useRuntimeVariables()
  const { authorizedAxios: axios, isAuthenticated } = useGeckoAuth()
  const { user } = useGeckoPermissions()
  const userId = user?.id
  const shouldFetch = Boolean(isAuthenticated && userId && siteId)
  const key: DocChatCapabilitiesKey | null = shouldFetch
    ? [
        docChatCapabilitiesUrl(POWER_BACKEND_AI_URL, siteId),
        userId ?? '',
        siteId,
      ]
    : null
  const { data, error, isLoading, mutate } = useSWR<
    DocChatCapabilityManifest,
    Error,
    DocChatCapabilitiesKey | null
  >(key, (currentKey) => fetchDocChatCapabilities(currentKey, axios))

  return {
    data: shouldFetch && !error ? data : undefined,
    error,
    isLoading: shouldFetch && isLoading,
    refresh: mutate,
  }
}
